import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { bootstrapSession, fetchCapabilities, logout } from '../app/store/authSlice';
import { fetchSocieties, initializeContext, resetContext } from '../app/store/contextSlice';
import { fetchAppConfig, setOperationBlockFromApiError } from '../app/store/operationBlockSlice';
import { enqueueToast } from '../app/store/uiSlice';
import { canSwitchSocietyContext } from '../shared/constants/roles';
import { storage } from '../shared/lib/storage';

const SessionCoordinator = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const status = useSelector((state) => state.auth.status);
  const role = useSelector((state) => state.auth.role);
  const user = useSelector((state) => state.auth.user);
  const capabilitiesStatus = useSelector((state) => state.auth.capabilities?.status || 'idle');

  const societyIdFromUrl = new URLSearchParams(location.search).get('societyId');

  useEffect(() => {
    dispatch(bootstrapSession());
    dispatch(fetchAppConfig({ surface: 'WEB_ADMIN' }));
  }, [dispatch]);

  useEffect(() => {
    const handleOperationBlocked = (event) => {
      dispatch(setOperationBlockFromApiError(event.detail));
    };

    window.addEventListener('parktek:operation-blocked', handleOperationBlocked);

    return () => {
      window.removeEventListener('parktek:operation-blocked', handleOperationBlocked);
    };
  }, [dispatch]);

  useEffect(() => {
    const handleUnauthorized = () => {
      dispatch(logout());
      dispatch(resetContext());
      dispatch(
        enqueueToast({
          severity: 'error',
          message: 'Your session has expired. Please log in again.',
        }),
      );
    };

    window.addEventListener('parktek:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('parktek:unauthorized', handleUnauthorized);
    };
  }, [dispatch]);

  useEffect(() => {
    if (status !== 'authenticated') {
      return undefined;
    }

    const expiresAt = storage.getSessionExpiresAt();
    if (!expiresAt) {
      return undefined;
    }

    const timeoutId = window.setTimeout(
      () => {
        dispatch(logout());
        dispatch(resetContext());
        dispatch(
          enqueueToast({
            severity: 'error',
            message: 'Your session has expired. Please log in again.',
          }),
        );
      },
      Math.max(expiresAt - Date.now(), 0),
    );

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [dispatch, status]);

  useEffect(() => {
    if (status === 'authenticated' && capabilitiesStatus === 'idle') {
      dispatch(fetchCapabilities());
    }
  }, [capabilitiesStatus, dispatch, status]);

  useEffect(() => {
    let isCancelled = false;

    const syncContext = async () => {
      if (status !== 'authenticated') {
        if (status === 'unauthenticated') {
          dispatch(resetContext());
        }
        return;
      }

      if (canSwitchSocietyContext(role)) {
        await dispatch(fetchSocieties());
      }

      if (!isCancelled) {
        dispatch(
          initializeContext({
            role,
            user,
            societyIdFromUrl,
          }),
        );
      }
    };

    syncContext();

    return () => {
      isCancelled = true;
    };
  }, [dispatch, role, societyIdFromUrl, status, user]);

  return null;
};

export default SessionCoordinator;
